import { ChangeEvent, FormEvent, useMemo, useState } from 'react'
import { faFolderTree, faPlus } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { CollectionPickerProps, DEFAULT_SUGGESTIONS } from './types'
import {
  Wrap,
  Trigger,
  Glyph,
  Backdrop,
  Dropdown,
  SuggestionRow,
  RowGlyph,
  InputRow,
  Input,
  InputAdd,
  ClearRow,
} from './styled'

/**
 * Click-to-pick collection chip. Shows the listing's current collection
 * label (if any). Tapping opens a dropdown with default suggestions, the
 * renter's existing collections, plus a free-text "create new" input.
 *
 * @param {CollectionPickerProps} props
 * @returns {JSX.Element | null}
 */
const CollectionPicker = ({
  listingId,
  size = 'md',
  knownSuggestions = DEFAULT_SUGGESTIONS,
}: CollectionPickerProps): JSX.Element | null => {
  const { profile, patchProfile } = useQuickPassProfile()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [draftValue, setDraftValue] = useState<string>('')

  const collections = useMemo<Record<string, string>>(
    () => profile?.listingCollections ?? {},
    [profile],
  )

  const knownCollections = useMemo<string[]>(() => {
    const fromProfile = Array.from(new Set(Object.values(collections)))
    const merged = [...fromProfile]
    for (const suggestion of knownSuggestions) {
      if (merged.includes(suggestion) === false) {
        merged.push(suggestion)
      }
    }
    return merged
  }, [collections, knownSuggestions])

  if (profile === null) {
    return null
  }

  const currentCollection = collections[listingId] ?? null

  const persistCollection = (next: string | null): void => {
    const nextCollections: Record<string, string> = { ...collections }
    if (next === null) {
      delete nextCollections[listingId]
    } else {
      nextCollections[listingId] = next
    }
    void patchProfile({ listingCollections: nextCollections })
  }

  const handleTriggerClick = (event: {
    stopPropagation: () => void
    preventDefault: () => void
  }): void => {
    event.stopPropagation()
    event.preventDefault()
    setIsOpen(previous => previous === false)
  }

  const handleSelect = (next: string): void => {
    persistCollection(next)
    setIsOpen(false)
  }

  const handleClear = (): void => {
    persistCollection(null)
    setIsOpen(false)
  }

  const handleSubmitNew = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    const trimmed = draftValue.trim()
    if (trimmed === '') {
      return
    }
    persistCollection(trimmed)
    setDraftValue('')
    setIsOpen(false)
  }

  const handleDraftChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDraftValue(event.target.value)
  }

  const handleBackdropClick = (): void => {
    setIsOpen(false)
  }

  const triggerLabel =
    currentCollection !== null ? currentCollection : 'Add to collection'
  const triggerGlyph = currentCollection !== null ? faFolderTree : faPlus
  const isDraftEmpty = draftValue.trim() === ''

  return (
    <Wrap>
      <Trigger
        type="button"
        size={size}
        hasValue={currentCollection !== null}
        onClick={handleTriggerClick}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Glyph aria-hidden="true">
          <Icon icon={triggerGlyph} />
        </Glyph>
        {triggerLabel}
      </Trigger>

      {isOpen === true ? (
        <>
          <Backdrop onClick={handleBackdropClick} />
          <Dropdown role="listbox">
            {knownCollections.map(name => (
              <SuggestionRow
                key={name}
                type="button"
                isCurrent={name === currentCollection}
                onClick={() => handleSelect(name)}
              >
                <RowGlyph aria-hidden="true">
                  <Icon icon={faFolderTree} />
                </RowGlyph>
                {name}
              </SuggestionRow>
            ))}
            {currentCollection !== null ? (
              <ClearRow type="button" onClick={handleClear}>
                Remove from collection
              </ClearRow>
            ) : null}
            <InputRow onSubmit={handleSubmitNew}>
              <Input
                value={draftValue}
                onChange={handleDraftChange}
                placeholder="New collection name"
                aria-label="New collection name"
              />
              <InputAdd type="submit" disabled={isDraftEmpty === true}>
                Add
              </InputAdd>
            </InputRow>
          </Dropdown>
        </>
      ) : null}
    </Wrap>
  )
}

export default CollectionPicker
